
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { familyInviteService } from '@/services/family';
import { familyMembershipService } from '@/services/family';
import { UserPlus } from 'lucide-react';
import { JoinFamilyDialog } from './dialogs/JoinFamilyDialog';
import { Profile } from '@/types/database';

type FamilyManagementProps = {
  profile: Profile;
  userId: string;
  isFamilyAdmin: boolean;
  isSingleMemberFamily: boolean;
  inviteCode: string | null;
  canSwitchFamily: boolean;
  switchFamilyWarning: string | null;
};

export const FamilyManagement: React.FC<FamilyManagementProps> = ({
  profile,
  userId,
  isFamilyAdmin,
  isSingleMemberFamily,
  inviteCode,
  canSwitchFamily,
  switchFamilyWarning,
}) => {
  const [isJoiningFamily, setIsJoiningFamily] = useState(false);
  const joinFamilyTriggerRef = useRef<HTMLButtonElement>(null);

  const handleJoinFamily = async (inviteCode: string) => {
    if (!userId) return;
    
    setIsJoiningFamily(true);
    
    try {
      const result = await familyInviteService.joinFamilyByInviteCode(userId, inviteCode);
      
      if (result.success) {
        toast.success(result.message);
        // Reload page to show new family info
        window.location.reload();
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      console.error('Error joining family:', error);
      throw error;
    } finally {
      setIsJoiningFamily(false);
    }
  };

  const handleOpenJoinDialog = async () => {
    // If the user is in a family and is admin, check if they can switch
    if (profile?.current_family_id && isFamilyAdmin) {
      const { canLeave, message } = await familyMembershipService.canUserLeaveFamilyAsAdmin(userId);
      
      if (!canLeave) {
        toast.error(message || "You cannot leave your family because you are the admin.");
        return;
      }
    }
    
    // Open the dialog if allowed
    if (joinFamilyTriggerRef.current) {
      joinFamilyTriggerRef.current.click();
    }
  };

  if (!profile.current_family_id) {
    return (
      <JoinFamilyDialog
        onJoinFamily={handleJoinFamily}
        isJoiningFamily={isJoiningFamily}
        hasFamily={false}
      />
    );
  }

  // Return only the join family dialog trigger when user has a family
  return (
    <>
      <JoinFamilyDialog
        onJoinFamily={handleJoinFamily}
        isJoiningFamily={isJoiningFamily}
        hasFamily={true}
        isSingleMemberFamily={isSingleMemberFamily}
        switchWarning={switchFamilyWarning}
        triggerRef={joinFamilyTriggerRef}
        customTrigger={true}
      />
      <Button 
        size="sm" 
        onClick={handleOpenJoinDialog}
        disabled={!canSwitchFamily}
        title={!canSwitchFamily ? (switchFamilyWarning || "Cannot switch families") : ""}
        variant="secondary"
        className="gap-1"
      >
        <UserPlus className="h-4 w-4" />
        Join Family circle
      </Button>
    </>
  );
};
